import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';

interface UserPopoverProps {
    companyName: string;
    email: string;
    // Whether the "change company" action is offered. Hidden for host-fixed sessions: the AcombaGo
    // portal and the Acomba desktop launch, where the société is dictated by the host app.
    canChangeCompany?: boolean;
    // Whether the logout action is offered. Hidden only for the AcombaGo portal iframe (the host page
    // owns that session's lifecycle). Shown for the Acomba desktop integration too — signing out ends
    // the SSO account, not the open société, so the user can hand off to a different SSO account
    // without waiting out the desktop session's TTL (PR #261 review).
    canLogout?: boolean;
    onSettings: () => void;
    onChangeCompany: () => void;
    onLogout: () => void;
}

export const UserPopover: React.FC<UserPopoverProps> = ({
    companyName,
    email,
    canChangeCompany = true,
    canLogout = true,
    onSettings,
    onChangeCompany,
    onLogout,
}) => {
        const { t } = useTranslation();
        return (
            <div
                className="w-[240px] p-m rounded-lg shadow-medium ring-1 ring-line-subtle bg-surface"
                role="menu"
                aria-label={t(TranslationKey.Common.UserMenuAriaLabel)}
            >
                <div>
                    <div className="text-content-secondary text-s truncate" data-testid="user-popover-company-name">{companyName}</div>
                    <div className="text-content-secondary text-s truncate">{email}</div>
                </div>
                <hr className="border-t border-line-subtle my-m" />
                <div className="flex flex-col gap-xs">
                    <button
                        className="text-content text-s py-s px-s text-left rounded transition-colors hover:bg-overlay-weak cursor-pointer"
                        data-testid="header-settings-btn"
                        onClick={onSettings}
                        role="menuitem"
                    >
                        {t(TranslationKey.Header.Settings as string)}
                    </button>
                    {canChangeCompany && (
                        <button
                            className="text-content text-s py-s px-s text-left rounded transition-colors hover:bg-overlay-weak cursor-pointer"
                            data-testid="user-popover-change-company-btn"
                            onClick={onChangeCompany}
                            role="menuitem"
                        >
                            {t(TranslationKey.Header.ChangeCompany as string)}
                        </button>
                    )}
                    {canLogout && (
                        <button
                            className="text-content text-s py-s px-s text-left rounded transition-colors hover:bg-overlay-weak cursor-pointer"
                            data-testid="user-popover-logout-btn"
                            onClick={onLogout}
                            role="menuitem"
                        >
                            {t(TranslationKey.Header.Logout as string)}
                        </button>
                    )}
                </div>
            </div>
        );
    };
